import { useEffect, useMemo, useState } from "react";
import { CloseIcon, InfoIcon } from "./icons";
import type { AuthUser } from "../types/auth";

type AuthMode = "login" | "register";
type AuthTarget = "notifications" | "price-alerts";

interface CaptchaPayload {
  captcha_id: string;
  expires_in: number;
  image_svg: string;
}

interface AuthDialogProps {
  isOpen: boolean;
  onAuthenticated: (user: AuthUser) => void;
  onClose: () => void;
  target: AuthTarget | null;
}

const TARGET_COPY: Record<AuthTarget, { title: string; description: string }> = {
  notifications: {
    title: "登录后配置上新通知",
    description: "先登录或注册账号，验证通过后再进入上新通知设置。",
  },
  "price-alerts": {
    title: "登录后配置价格通知",
    description: "先登录或注册账号，验证通过后再进入价格通知设置。",
  },
};

export default function AuthDialog({
  isOpen,
  onAuthenticated,
  onClose,
  target,
}: AuthDialogProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [captcha, setCaptcha] = useState<CaptchaPayload | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const copy = useMemo(() => {
    if (!target) {
      return {
        title: "登录或注册账号",
        description: "登录后可安全保存 Bark Key，并继续配置通知。",
      };
    }
    return TARGET_COPY[target];
  }, [target]);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setCaptchaCode("");
    setMessage("");
    setShowHelp(false);
  };

  const loadCaptcha = async () => {
    try {
      const res = await fetch("/api/auth/captcha");
      const data = await res.json();
      setCaptcha(data);
      setCaptchaCode("");
    } catch (error) {
      console.error("Failed to load captcha:", error);
      setMessage("验证码加载失败，请刷新重试");
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
    void loadCaptcha();
  }, [isOpen, mode]);

  const handleAuth = async () => {
    if (!email.trim() || !password.trim() || !captchaCode.trim() || !captcha?.captcha_id) {
      setMessage("请输入邮箱、密码和图形验证码");
      return;
    }

    setSubmitting(true);
    setMessage("");
    try {
      const res = await fetch(`/api/auth/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          password,
          captcha_id: captcha.captcha_id,
          captcha_code: captchaCode.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(data.error || "操作失败");
        await loadCaptcha();
        return;
      }
      onAuthenticated(data.user);
      onClose();
    } catch (error) {
      console.error("Auth request failed:", error);
      setMessage("操作失败，请重试");
      await loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleAuth();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-slideUp"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#1D1D1F]">{copy.title}</h2>
              <p className="text-sm text-gray-500 mt-1">{copy.description}</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  mode === "login"
                    ? "bg-[#0071E3] text-white"
                    : "bg-white border border-gray-200 text-gray-600"
                }`}
              >
                登录
              </button>
              <button
                type="button"
                onClick={() => setMode("register")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  mode === "register"
                    ? "bg-[#0071E3] text-white"
                    : "bg-white border border-gray-200 text-gray-600"
                }`}
              >
                注册
              </button>
            </div>

            <form
              className="space-y-3"
              autoComplete="on"
              onSubmit={handleSubmit}
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="邮箱"
                autoComplete="username"
                autoCapitalize="none"
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
              />
              <input
                type="password"
                name="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="密码（至少 8 位）"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
              />

              <div className="space-y-2">
                <label className="block text-xs text-gray-500">图形验证码</label>
                <div className="flex gap-2 items-center">
                  <div className="h-[52px] min-w-[148px] rounded-xl border border-gray-200 overflow-hidden bg-[#f8fbff] flex items-center justify-center">
                    {captcha?.image_svg ? (
                      <img
                        src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(captcha.image_svg)}`}
                        alt="captcha"
                        className="block h-full w-full"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">加载中...</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => void loadCaptcha()}
                    className="px-3 py-2 text-xs text-[#0071E3] bg-[#F3F8FE] rounded-lg hover:bg-[#E8F1FD]"
                  >
                    刷新验证码
                  </button>
                </div>
                <input
                  type="text"
                  name="captcha"
                  value={captchaCode}
                  onChange={(event) => setCaptchaCode(event.target.value.toUpperCase())}
                  placeholder="输入图形验证码"
                  autoComplete="off"
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm tracking-[0.25em] uppercase focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 bg-[#0071E3] text-white rounded-xl font-semibold hover:bg-[#0077ED] transition-colors disabled:opacity-50"
              >
                {submitting ? "提交中..." : mode === "login" ? "登录" : "注册"}
              </button>
            </form>

            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="mt-4 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <InfoIcon className="w-3 h-3" />
              {showHelp ? "收起说明" : "为什么需要登录和验证码？"}
            </button>

            {showHelp && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-2">
                <p>1. 登录后，通知配置和历史记录会绑定到你的账号。</p>
                <p>2. Bark Key 只会保存在服务端，不再泄露到浏览器本地存储或 URL。</p>
                <p>3. 图形验证码用于降低批量注册和撞库请求风险。</p>
              </div>
            )}

            {message && (
              <div className="mt-3 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
